/**
 * Shared extraction of a human-usable message from an API error.
 */

/**
 * Pull the most specific message out of an API error response.
 *
 * The backend answers in the Helix error envelope, and the useful text sits at
 * different depths depending on what failed:
 *
 *   - serializer/field errors: `details.fields.<field>[0]`
 *     (e.g. "Company Group with this name already exists.")
 *   - view-raised errors:      `error`
 *   - DRF defaults:            `detail`
 *
 * Reading only the top level leaves the user with "Request failed with status
 * code 400", which says nothing about the one thing they could fix. The generic
 * "Validation failed" wrapper is skipped for the same reason.
 *
 * @param err - The thrown error (usually an AxiosError)
 * @param fallback - Message to use when the response carries nothing better
 * @returns The most specific message available
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
  if (data) {
    const fields = (data['details'] as { fields?: Record<string, string[]> } | undefined)?.fields;
    const firstField = fields && Object.values(fields)[0]?.[0];
    const message = firstField
      || (data['name'] as string[] | undefined)?.[0]
      || (data['error'] as string | undefined)
      || (data['detail'] as string | undefined)
      || (data['message'] as string | undefined);
    if (message && message !== 'Validation failed') return message;
  }
  return err instanceof Error ? err.message : fallback;
}
